import { useEffect, useState, useRef } from "react"
import { Switch, FormControlLabel, Typography } from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import { Toast } from "primereact/toast"
import dayjs from "dayjs"
import { CalendarDays, Upload, Download, ChevronLeft, ChevronRight, X, ChevronDown } from "lucide-react"
import axios from "axios"
import * as XLSX from "xlsx"
import { ClimbingBoxLoader } from "react-spinners"

const Revision = () => {
  const toast = useRef(null)
  const [formData, setFormData] = useState({
    siteCode: [],
    projectName: [],
    state: [],
    location: [],
    preclosureRange: "",
  })

  const [dropdownOptions, setDropdownOptions] = useState({
    site_codes: [],
    project_names: [],
    states: [],
    locations: [],
    pro_closure_date_range: [],
  })

  // Store original options that never get filtered
  const [originalDropdownOptions, setOriginalDropdownOptions] = useState({
    site_codes: [],
    project_names: [],
    states: [],
    locations: [],
    pro_closure_date_range: [],
  })

  // Add cascading filter state
  const [cascadingOptions, setCascadingOptions] = useState({
    site_codes: [],
    project_names: [],
    states: [],
    locations: [],
    pro_closure_date_range: [],
  })

  // Add filtered data state to store the current filtered dataset
  const [originalDataset, setOriginalDataset] = useState([])

  // Track which dropdown is open
  const [openDropdown, setOpenDropdown] = useState(null)
  const [lastOpenDropdown, setLastOpenDropdown] = useState(null)

  // Search terms for each dropdown
  const [searchTerms, setSearchTerms] = useState({
    siteCode: "",
    projectName: "",
    state: "",
    location: "",
    preclosureRange: "",
  })

  const searchInputRef = useRef(null)
  const dropdownScrollRef = useRef(null)
  const scrollPositions = useRef({})

  const [fromMonth, setFromMonth] = useState(null)
  const [toMonth, setToMonth] = useState(null)
  const [preclosure, setPreclosure] = useState(false)
  const [tableData, setTableData] = useState([])
  const [, setOriginalData] = useState([])
  const [uploadedFile, setUploadedFile] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  // New state for enhanced features
  const [selectedRows, setSelectedRows] = useState(new Set())
  const [showPayrollSection, setShowPayrollSection] = useState(false)
  const [payrollFile, setPayrollFile] = useState(null)

  // New state for payroll dump download and upload
  const [isDownloadingPayroll, setIsDownloadingPayroll] = useState(false)
  const [isUploadingPayroll, setIsUploadingPayroll] = useState(false)

  // State to track if any dropdown is open for table positioning
  const [isAnyDropdownOpen, setIsAnyDropdownOpen] = useState(false)

  // New state for success modal
  const [showSuccessModal, setShowSuccessModal] = useState(false)

  const client_id = sessionStorage.getItem("client_id")
  const role_id = sessionStorage.getItem("user_role_id")
  const token = sessionStorage.getItem("token")

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const rowsPerPage = 12
  const paginatedData = tableData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
  const totalPages = Math.ceil(tableData.length / rowsPerPage)

  // Toast helper functions - NO AUTO-HIDE, MANUAL CLOSE ONLY
  const showSuccess = (summary, detail, options) => {
    toast.current?.show({
      severity: "success",
      summary: summary,
      detail: detail,
      life: 0, // Never auto-hide
      closable: true,
      sticky: true,
      ...options,
    })
  }

  const showError = (summary, detail) => {
    toast.current?.show({
      severity: "error",
      summary: summary,
      detail: detail,
      life: 0, // Never auto-hide
      closable: true,
      sticky: true,
    })
  }

  const showWarn = (summary, detail) => {
    toast.current?.show({
      severity: "warn",
      summary: summary,
      detail: detail,
      life: 0, // Never auto-hide
      closable: true,
      sticky: true,
    })
  }

  const showInfo = (summary, detail) => {
    toast.current?.show({
      severity: "info",
      summary: summary,
      detail: detail,
      life: 0, // Never auto-hide
      closable: true,
      sticky: true,
    })
  }

  // Helper function to properly download Excel files from API responses
  const downloadExcelFile = (responseData, defaultFilename, contentDisposition = null) => {
    try {
      // Create blob with proper Excel MIME type
      const blob = new Blob([responseData], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })

      // Extract filename from content-disposition header if available
      let filename = defaultFilename
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "").trim()
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", filename)

      // Append to body, click, and cleanup
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up the blob URL
      setTimeout(() => {
        window.URL.revokeObjectURL(url)
      }, 100)

      return filename
    } catch (error) {
      console.error("Error downloading Excel file:", error)
      throw error
    }
  }

  // Helper function to get value regardless of case sensitivity
  const getFieldValue = (item, fieldOptions) => {
    for (const field of fieldOptions) {
      // Check for exact field match
      if (item[field] !== undefined) {
        return item[field]
      }
      // Check for case-insensitive field match
      const fieldLower = field.toLowerCase()
      for (const key of Object.keys(item)) {
        if (key.toLowerCase() === fieldLower) {
          return item[key]
        }
      }
    }
    return null
  }

  // Helper function to extract specific field values from row data
  const getTableFieldValue = (row, fieldType) => {
    switch (fieldType) {
      case "project_code":
        return getFieldValue(row, [
          "project_code",
          "Project_code",
          "site_code",
          "Site_code",
          "projectcode",
          "ProjectCode",
        ])
      case "project_name":
        return getFieldValue(row, ["project_name", "Project_name", "Project_Name", "projectname", "ProjectName"])
      case "state":
        return getFieldValue(row, ["state", "State", "STATE"])
      case "location":
        return getFieldValue(row, ["location", "Location", "site_location", "Site_Location", "LOCATION"])
      case "period":
        // Handle period field from backend
        const periodValue = getFieldValue(row, [
          "period",
          "Period",
          "PERIOD",
          "month_year",
          "Month_Year",
          "monthYear",
          "MonthYear",
          "date_period",
          "Date_Period",
          "payroll_period",
          "Payroll_Period",
        ])

        // If period value exists, format it properly
        if (periodValue) {
          // If it's already in MMM YYYY format, return as is
          if (typeof periodValue === "string" && periodValue.match(/^[A-Za-z]{3}\s\d{4}$/)) {
            return periodValue
          }

          // If it's in YYYY-MM-DD or YYYY-MM format, convert to MMM YYYY
          if (
            typeof periodValue === "string" &&
            (periodValue.match(/^\d{4}-\d{2}-\d{2}$/) || periodValue.match(/^\d{4}-\d{2}$/))
          ) {
            return dayjs(periodValue).format("MMM YYYY")
          }

          // If it's a date object, format it
          if (periodValue instanceof Date || dayjs.isDayjs(periodValue)) {
            return dayjs(periodValue).format("MMM YYYY")
          }

          // Return as is if it's already formatted
          return periodValue
        }

        // Fallback to current month if no period found
        return dayjs().format("MMM YYYY")
      default:
        return ""
    }
  }

  // Handle row selection
  const handleRowSelect = (rowIndex) => {
    const newSelected = new Set(selectedRows)
    if (newSelected.has(rowIndex)) {
      newSelected.delete(rowIndex)
    } else {
      newSelected.add(rowIndex)
    }
    setSelectedRows(newSelected)
  }

  // Handle select all
  const handleSelectAll = () => {
    if (selectedRows.size === tableData.length) {
      setSelectedRows(new Set())
    } else {
      setSelectedRows(new Set(Array.from({ length: tableData.length }, (_, i) => i)))
    }
  }

  // Export selected rows to Excel
  const handleExport = () => {
    if (selectedRows.size === 0) {
      showWarn("Export Warning", "Please select at least one row to export.")
      return
    }

    try {
      const selectedData = Array.from(selectedRows).map((index) => ({
        "S.No": index + 1,
        "Month/Year": getTableFieldValue(tableData[index], "period"), // Use backend period
        "Project Code": getTableFieldValue(tableData[index], "project_code"),
        "Project Name": getTableFieldValue(tableData[index], "project_name"),
        State: getTableFieldValue(tableData[index], "state"),
        Location: getTableFieldValue(tableData[index], "location"),
      }))

      const ws = XLSX.utils.json_to_sheet(selectedData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, "Filtered Data")
      XLSX.writeFile(wb, "filter_triggered.xlsx")

      showSuccess("Export Successful", `Successfully exported ${selectedRows.size} rows to Excel file.`)
    } catch (error) {
      showError("Export Failed", "Failed to export data to Excel. Please try again.")
    }
  }

  // Handle submit button
  const handleSubmit = () => {
    if (selectedRows.size === 0) {
      showWarn("Selection Required", "Please select at least one row to proceed.")
      return
    }
    setShowPayrollSection(true)
  }

  // Handle payroll file upload
  const handlePayrollFileUpload = (e) => {
    const file = e.target.files[0]
    if (file && file.name.endsWith(".xlsx")) {
      setPayrollFile(file)
    } else {
      e.target.value = null
    }
  }

  // Handle payroll file removal
  const handleRemovePayrollFile = () => {
    setPayrollFile(null)
    document.getElementById("payroll-upload").value = null
    showInfo("File Removed", "Payroll file has been removed.")
  }

  // Handle payroll template download with API integration - UPDATED
  const handleDownloadPayrollTemplate = async () => {
    if (!client_id) {
      showError("Authentication Error", "Client ID is required for payroll dump download.")
      return
    }

    if (selectedRows.size === 0) {
      showWarn("Missing Selection", "Please select at least one row to get project codes.")
      return
    }

    if (preclosure && !formData.preclosureRange) {
      showWarn("Missing Selection", "Please select a preclosure date range.")
      return
    }

    setIsDownloadingPayroll(true)

    try {
      const company_name = sessionStorage.getItem("company_name")

      const selectedData = Array.from(selectedRows).map((index) => tableData[index])
      const projectCodes = selectedData
        .map((row) => getTableFieldValue(row, "project_code"))
        .filter(Boolean)
        .join(",")

      const params = {
        client_id: client_id,
        company_name: company_name || "", // Use empty string if not found
        project_codes: projectCodes,
        from_date: fromMonth ? dayjs(fromMonth).format("YYYY-MM-DD") : "",
        to_date: toMonth ? dayjs(toMonth).format("YYYY-MM-DD") : "",
      }

      const response = await axios.get("https://api.rcube.tickbox.co.in/api/download_month_payrolldump/", {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "arraybuffer",
      })

      // Extract filename from response headers or create default name
      const contentDisposition = response.headers["content-disposition"]
      let filename = `payroll_dump_${dayjs().format("YYYY-MM-DD")}.xlsx`
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }

      // Use the helper function to download the file
      downloadExcelFile(response.data, filename, contentDisposition)
      showSuccess("Download Successful", `Successfully downloaded payroll dump file: ${filename}`, {
        life: 5000,
        sticky: false,
      })
    } catch (error) {
      console.error("Payroll download error:", error)
      if (error.response) {
        showError(
          "Download Failed",
          `Failed to download payroll dump: ${error.response.data?.message || error.response.statusText}`,
        )
      } else {
        showError("Download Failed", "Failed to download payroll dump. Please check your connection.")
      }
    } finally {
      setIsDownloadingPayroll(false)
    }
  }

  // Handle success modal OK button click
  const handleSuccessModalOk = () => {
    setShowSuccessModal(false)
    window.location.reload()
  }

  // Handle final submit with new payroll dump upload API - FIXED
  const handleFinalSubmit = async () => {
    if (!payrollFile) {
      showWarn("File Required", "Please upload the payroll dump file before final submission.")
      return
    }

    if (selectedRows.size === 0) {
      showWarn("Selection Required", "Please select at least one row from the table.")
      return
    }

    if (!client_id) {
      showError("Authentication Error", "Client ID is required. Please ensure you are logged in.")
      return
    }

    setIsUploadingPayroll(true)

    try {
      // Get the selected row data and extract IDs
      const selectedData = Array.from(selectedRows).map((index) => tableData[index])

      // Extract IDs from selected rows
      const selectedIds = selectedData
        .map((row, index) => {
          // Try different possible ID field names
          return row.id || row.ID || row.Id || row.emp_id || row.employee_id || row.EMP_ID || index
        })
        .filter(Boolean)

      if (selectedIds.length === 0) {
        showError("No Valid IDs", "No valid IDs found in selected rows.")
        setIsUploadingPayroll(false)
        return
      }

      // Create comma-separated string of IDs
      const idsString = selectedIds.join(",")

      // Create FormData for the upload
      const uploadFormData = new FormData()
      uploadFormData.append("client_id", client_id)
      uploadFormData.append("id", idsString)
      uploadFormData.append("revision_dump", payrollFile)

      // Upload to the new API endpoint
      const response = await axios.post("https://api.rcube.tickbox.co.in/api/upload_payrolldump/", uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        responseType: "arraybuffer", // Changed to arraybuffer for proper binary handling
      })

      console.log("Upload response:", {
        status: response.status,
        headers: response.headers,
        contentType: response.headers["content-type"],
        contentDisposition: response.headers["content-disposition"],
      })

      // Check if response contains an error file (even with 200 status)
      const contentDisposition = response.headers["content-disposition"] || response.headers["Content-Disposition"]
      const contentType = response.headers["content-type"] || response.headers["Content-Type"]

      // If there's a content-disposition header with "attachment" or content-type suggests a file download,
      // it's likely an error file even with 200 status
      const isErrorFile =
        contentDisposition &&
        contentDisposition.includes("attachment") &&
        (contentDisposition.includes("Error") || contentDisposition.includes("error"))

      const isExcelFile =
        contentType &&
        (contentType.includes("application/vnd.ms-excel") ||
          contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"))

      if (isErrorFile || (isExcelFile && contentDisposition && contentDisposition.includes("attachment"))) {
        console.log("Detected error file in 200 response, initiating download...")

        try {
          // Generate filename with timestamp for uniqueness
          const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19)
          const defaultFilename = `payroll_upload_errors_${timestamp}.xlsx`

          // Use the helper function to download the error file
          const downloadedFilename = downloadExcelFile(response.data, defaultFilename, contentDisposition)
          showError("Upload Failed - Error File Downloaded", `"${downloadedFilename}" has been downloaded.`)

          // Remove the payroll file after error file download
          setPayrollFile(null)
          document.getElementById("payroll-upload").value = null
        } catch (downloadError) {
          console.error("Error downloading Excel error file:", downloadError)
          showError("Upload Failed", "Upload failed and error file could not be downloaded. Please contact support.")

          // Remove the payroll file after failed error file download
          setPayrollFile(null)
          document.getElementById("payroll-upload").value = null
        }
      } else {
        // Successful upload - Show success modal instead of toast
        setShowSuccessModal(true)

        // Remove the payroll file after successful upload
        setPayrollFile(null)
        document.getElementById("payroll-upload").value = null

        // Reset form after successful upload
        setShowPayrollSection(false)
        setSelectedRows(new Set())
      }
    } catch (error) {
      console.error("Payroll upload error:", error)
      if (error.response) {
        console.log("Error response received:", {
          status: error.response.status,
          headers: error.response.headers,
          dataType: typeof error.response.data,
          dataSize: error.response.data?.byteLength || "unknown",
        })

        // Handle actual HTTP error responses (4xx, 5xx)
        const isValidationError = error.response.status >= 400 && error.response.status < 500
        if (isValidationError && error.response.data) {
          try {
            const contentType = error.response.headers["content-type"] || ""
            if (contentType.includes("application/json") || contentType.includes("text/plain")) {
              let errorMessage = "An error occurred."
              let decodedString = ""
              try {
                // Convert arraybuffer to string
                const decoder = new TextDecoder("utf-8")
                decodedString = decoder.decode(error.response.data)
                // Try to parse JSON
                const parsed = JSON.parse(decodedString)
                if (parsed.message) {
                  errorMessage = parsed.message
                } else if (parsed.error) {
                  errorMessage = parsed.error
                } else {
                  // fallback to full json as string
                  errorMessage = decodedString
                }
              } catch (parseError) {
                console.error("Error parsing JSON error response:", parseError)
                errorMessage = decodedString || "An unknown error occurred."
              }

              showError("Upload Failed", errorMessage)
            } else {
              console.log("Detected validation error with file response, attempting Excel download...")

              const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19)
              const defaultFilename = `payroll_upload_errors_${timestamp}.xlsx`

              const contentDisposition =
                error.response.headers["content-disposition"] || error.response.headers["Content-Disposition"]

              const downloadedFilename = downloadExcelFile(error.response.data, defaultFilename, contentDisposition)
              showError("Upload Failed - Error File Downloaded", `"${downloadedFilename}" has been downloaded.`)
            }
          } catch (downloadError) {
            console.error("Error downloading Excel error file:", downloadError)
            showError("Upload Failed", "Upload failed and error file could not be downloaded. Please contact support.")
          }
        } else {
          // Handle non-file error responses
          console.log("Non-file error response")
          if (error.response.status === 500) {
            showError("Upload Failed", "Something went wrong!!!")
          } else {
            showError("Upload Failed", "An error occurred during upload. Please try again.")
          }
        }

        // Remove the payroll file after any error
        setPayrollFile(null)
        document.getElementById("payroll-upload").value = null
      } else if (error.request) {
        // Remove the payroll file after network error
        setPayrollFile(null)
        document.getElementById("payroll-upload").value = null
        showError("Network Error", "No response received from server. Please check your network connection.")
      } else {
        // Remove the payroll file after request error
        setPayrollFile(null)
        document.getElementById("payroll-upload").value = null
        showError("Request Error", `Error setting up request: ${error.message}`)
      }
    } finally {
      setIsUploadingPayroll(false)
    }
  }

  const handleApply = async () => {
  if (!fromMonth) {
    showError("Validation Error", "Please select the From (MMM/YYYY) date.")
    return
  }
  if (!toMonth) {
    showError("Validation Error", "Please select the To (MMM/YYYY) date.")
    return
  }

    setIsLoading(true)
    // Validate required parameters for file upload
    if (uploadedFile && !client_id) {
      showError("Authentication Error", "Client ID is required for file upload. Please ensure you are logged in.")
      setIsLoading(false)
      return
    }

    try {
      if (uploadedFile) {
        // File upload with site template
        const fileFormData = new FormData()
        // Mandatory parameters
        fileFormData.append("client_id", client_id)
        fileFormData.append("revision_filter_file", uploadedFile)

        // Optional parameters - only add if they have values
        if (preclosure && formData.preclosureRange) {
          fileFormData.append("pre_closure_date_range", formData.preclosureRange)
        }

        if (fromMonth) {
          fileFormData.append("from_date", dayjs(fromMonth).format("YYYY-MM-01"))
        }

        if (toMonth) {
          fileFormData.append("to_date", dayjs(toMonth).format("YYYY-MM-01"))
        }

        console.log("Uploading file with parameters:")
        console.log("- client_id:", client_id)
        console.log("- revision_filter_file:", uploadedFile.name)
        console.log(
          "- pre_closure_date_range:",
          preclosure && formData.preclosureRange ? formData.preclosureRange : "Not provided",
        )
        console.log("- from_date:", fromMonth ? dayjs(fromMonth).format("YYYY-MM-01") : "Not provided")
        console.log("- to_date:", toMonth ? dayjs(toMonth).format("YYYY-MM-01") : "Not provided")

        try {
          // Upload file to backend
          const res = await axios.post("https://api.rcube.tickbox.co.in/api/projectcodeFilter/", fileFormData, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          })

          console.log("File upload API response:", res)

          // Process the response directly from backend
          if (res.data && res.data.data) {
            setTableData(res.data.data)
            setCurrentPage(1)
            setSelectedRows(new Set())
            console.log("Successfully loaded", res.data.data.length, "records from file upload")

            // Clear the uploaded file after successful processing
            setUploadedFile(null)
            document.getElementById("site-upload").value = null
          } else {
            console.log("No data in response:", res.data)
            showWarn("No Data", "No data returned from server.")
            setTableData([])
          }
        } catch (uploadError) {
          console.error("File upload error:", uploadError)
          if (uploadError.response?.data) {
            let errorMessage = "An error occurred."
            try {
              if (typeof uploadError.response.data === "string") {
                // If plain string, use directly
                errorMessage = uploadError.response.data
              } else if (typeof uploadError.response.data === "object") {
                // If JSON object
                if (uploadError.response.data.message) {
                  errorMessage = uploadError.response.data.message
                } else if (uploadError.response.data.error) {
                  errorMessage = uploadError.response.data.error
                } else {
                  // Fallback - show whole object as string
                  errorMessage = JSON.stringify(uploadError.response.data)
                }
              }
            } catch (parseError) {
              console.error("Error parsing error response:", parseError)
            }

            showError("Upload Failed", errorMessage)
          } else {
            showError("Upload Failed", "Upload failed. Please try again.")
          }

          // Clear the uploaded file after error
          setUploadedFile(null)
          document.getElementById("site-upload").value = null
          throw uploadError // Re-throw to be caught by outer catch
        }
      } else {
        // Regular filter without file upload - use FileStorageListView GET endpoint
        const params = {
          client_id: client_id, // Mandatory
        }

        // Add comma-separated values for selected filters
        if (formData.siteCode.length > 0) {
          params.project_code = formData.siteCode.join(",")
        }

        if (formData.projectName.length > 0) {
          params.project_name = formData.projectName.join(",")
        }

        if (formData.location.length > 0) {
          params.location = formData.location.join(",")
        }

        if (formData.state.length > 0) {
          params.state = formData.state.join(",")
        }

        // Add preclosure parameters if enabled
        if (preclosure) {
          //params.pre_closure_model = "true" // Send as string since it's a URL parameter
          if (formData.preclosureRange) {
            params.pre_closure_date_range = formData.preclosureRange
          }
        }

        // Add date parameters if selected
        if (fromMonth) {
          params.from_date = dayjs(fromMonth).format("YYYY-MM-01")
        }

        if (toMonth) {
          params.to_date = dayjs(toMonth).format("YYYY-MM-01")
        }

        console.log("Sending GET request to FileStorageListView with params:", params)

        try {
          // Make GET request to RevisionListView
          const res = await axios.get("https://api.rcube.tickbox.co.in/api/RevisionListView/", {
            params,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })

          console.log("FileStorageListView API response:", res)

          // Check different possible response structures
          let responseData = []
          if (res.data && Array.isArray(res.data)) {
            // If response is directly an array
            responseData = res.data
          } else if (res.data && res.data.data && Array.isArray(res.data.data)) {
            // If response has a data property that is an array
            responseData = res.data.data
          } else if (res.data && res.data.results && Array.isArray(res.data.results)) {
            // If response has a results property that is an array (common in paginated APIs)
            responseData = res.data.results
          }

          if (responseData.length > 0) {
            setTableData(responseData)
            setCurrentPage(1)
            setSelectedRows(new Set())
          } else {
            console.log("API returned empty data array:", res.data)
            showWarn("No Data Found", "No data returned from server. Please try different filter criteria.")
            setTableData([])
          }
        } catch (err) {
          console.error("FileStorageListView API error:", err)
          if (err.response) {
            console.error("Error response data:", err.response.data)
            console.error("Error response status:", err.response.status)
            console.error("Error response headers:", err.response.headers)

            if (err.response.status === 404) {
              showError(
                "API Error",
                "API endpoint not found (404). Please check if the FileStorageListView endpoint is correct.",
              )
            } else {
              showError(
                "API Error",
                `Failed to fetch data (${err.response.status}): ${
                  err.response.data.message || err.response.data.detail || err.response.statusText
                }`,
              )
            }
          } else if (err.request) {
            // Request was made but no response received
            console.error("No response received:", err.request)
            showError("Network Error", "No response received from server. Please check your network connection.")
          } else {
            // Something happened in setting up the request
            console.error("Error setting up request:", err.message)
            showError("Request Error", `Error setting up request: ${err.message}`)
          }
        }
      }

      setIsLoading(false)
    } catch (err) {
      if (err.response) {
        if (err.response.status === 404) {
          showError(
            "API Error",
            "API endpoint not found (404). The server endpoint may not exist or the URL might be incorrect.",
          )
        }
      }
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (client_id && role_id && token) {
      axios
        .get("https://api.rcube.tickbox.co.in/api/revision_filter/", {
          params: { client_id, role_id },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((res) => {
          const dataArray = res.data.data || []
          setOriginalData(dataArray)
          setOriginalDataset(dataArray)

          // Extract field names from the first record to determine the actual field names
          const firstRecord = dataArray[0] || {}

          // Find the site code field
          const siteCodeField = Object.keys(firstRecord).find(
            (key) =>
              key.toLowerCase().includes("project_code") ||
              key.toLowerCase().includes("projectcode") ||
              key.toLowerCase().includes("site_code") ||
              key.toLowerCase().includes("sitecode"),
          )

          // Find the project name field
          const projectNameField = Object.keys(firstRecord).find(
            (key) => key.toLowerCase().includes("project_name") || key.toLowerCase().includes("projectname"),
          )

          // Find the state field
          const stateField = Object.keys(firstRecord).find((key) => key.toLowerCase().includes("state"))

          // Find the location field
          const locationField = Object.keys(firstRecord).find(
            (key) => key.toLowerCase().includes("location") || key.toLowerCase().includes("site_location"),
          )

          const site_codes = [
            ...new Set(
              dataArray.map((item) =>
                getFieldValue(item, [siteCodeField, "project_code", "Project_code", "site_code", "Site_code"]),
              ),
            ),
          ].filter(Boolean)

          const project_names = [
            ...new Set(
              dataArray.map((item) =>
                getFieldValue(item, [projectNameField, "project_name", "Project_Name", "Project_name"]),
              ),
            ),
          ].filter(Boolean)

          const states = [
            ...new Set(dataArray.map((item) => getFieldValue(item, [stateField, "state", "State"]))),
          ].filter(Boolean)

          const locations = [
            ...new Set(
              dataArray.map((item) =>
                getFieldValue(item, [locationField, "location", "Location", "site_location", "Site_Location"]),
              ),
            ),
          ].filter(Boolean)

          const pro_closure_date_range = res.data.pre_closure_date_range || []

          const finalDropdownOptions = { site_codes, project_names, states, locations, pro_closure_date_range }

          setDropdownOptions(finalDropdownOptions)
          setOriginalDropdownOptions(finalDropdownOptions)
          setCascadingOptions(finalDropdownOptions)
        })
        .catch((err) => {
          console.error("Dropdown fetch error:", err)
          showError("Data Load Failed", "Failed to load filter options. Please refresh the page.")
        })
    }
  }, [client_id, role_id, token])

  useEffect(() => {
    if (openDropdown && searchInputRef.current) {
      // Auto-focus the search input when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
  }, [openDropdown])

  useEffect(() => {
    if (openDropdown && dropdownScrollRef.current && scrollPositions.current[openDropdown]) {
      // Restore scroll position when dropdown reopens
      setTimeout(() => {
        if (dropdownScrollRef.current) {
          dropdownScrollRef.current.scrollTop = scrollPositions.current[openDropdown]
        }
      }, 100)
    }
  }, [openDropdown])

  const handleSearchTermChange = (field, value) => {
    setSearchTerms((prev) => ({
      ...prev,
      [field]: value,
    }))

    // Maintain focus on the search input after state update
    setTimeout(() => {
      if (searchInputRef.current && openDropdown === field) {
        searchInputRef.current.focus()
      }
    }, 0)
  }

  const applyCascadingFilters = (currentFormData) => {
    // Use the original dataset from API for filtering
    if (originalDataset.length === 0) {
      console.log("No original dataset available for cascading filters")
      return
    }

    console.log("Applying cascading filters with:", currentFormData)
    console.log("Original dataset length:", originalDataset.length)

    // Filter based on site code selection - affects project names, states, AND locations
    if (currentFormData.siteCode.length > 0) {
      console.log("Filtering by site codes:", currentFormData.siteCode)

      const siteFilteredData = originalDataset.filter((item) => {
        const itemSiteCode = getFieldValue(item, [
          "project_code",
          "Project_code",
          "site_code",
          "Site_code",
          "projectcode",
          "ProjectCode",
        ])
        return currentFormData.siteCode.includes(itemSiteCode)
      })

      console.log("Site filtered data:", siteFilteredData.length, "records")

      // Update project names based on selected site codes
      const filteredProjectNames = [
        ...new Set(
          siteFilteredData.map((item) =>
            getFieldValue(item, ["project_name", "Project_name", "Project_Name", "projectname", "ProjectName"]),
          ),
        ),
      ].filter(Boolean)

      // Also update states and locations based on selected site codes
      const filteredStates = [
        ...new Set(siteFilteredData.map((item) => getFieldValue(item, ["state", "State", "STATE"]))),
      ].filter(Boolean)

      const filteredLocations = [
        ...new Set(
          siteFilteredData.map((item) =>
            getFieldValue(item, ["location", "Location", "site_location", "Site_Location", "LOCATION"]),
          ),
        ),
      ].filter(Boolean)

      console.log("Filtered project names:", filteredProjectNames)
      console.log("Filtered states:", filteredStates)
      console.log("Filtered locations:", filteredLocations)

      setCascadingOptions((prev) => ({
        ...prev,
        project_names: filteredProjectNames,
        states: filteredStates,
        locations: filteredLocations,
      }))

      // Clear dependent fields when site code changes and filter existing selections
      const previousSiteCodes = formData.siteCode
      if (JSON.stringify(previousSiteCodes.sort()) !== JSON.stringify(currentFormData.siteCode.sort())) {
        setFormData((prevData) => ({
          ...prevData,
          projectName: prevData.projectName.filter((name) => filteredProjectNames.includes(name)),
          state: prevData.state.filter((state) => filteredStates.includes(state)),
          location: prevData.location.filter((location) => filteredLocations.includes(location)),
        }))
      }
    } else {
      // Reset to original options if no filters
      setCascadingOptions((prev) => ({
        ...prev,
        project_names: originalDropdownOptions.project_names,
        states: originalDropdownOptions.states,
        locations: originalDropdownOptions.locations,
      }))
    }
  }

  // Function to update dropdown options based on current selections
  const updateDropdownOptions = (updatedFormData) => {
    applyCascadingFilters(updatedFormData)
  }

  // Custom dropdown handler
  const toggleDropdown = (field) => {
    if (openDropdown === field) {
      // Closing the current dropdown - trigger dependency filtering
      setOpenDropdown(null)
      setLastOpenDropdown(field)
      setIsAnyDropdownOpen(false)
      updateDropdownOptions(formData)
    } else {
      // Opening a new dropdown
      if (openDropdown !== null && lastOpenDropdown !== null) {
        // If another dropdown was open, trigger dependency filtering first
        updateDropdownOptions(formData)
      }
      setOpenDropdown(field)
      setLastOpenDropdown(field)
      setIsAnyDropdownOpen(true)
    }
  }

  const handleOptionSelect = (field, option) => {
    const currentScrollTop = dropdownScrollRef.current?.scrollTop || 0

    const currentValues = [...formData[field]]
    const index = currentValues.indexOf(option)
    if (index === -1) {
      // Add the option if not already selected
      currentValues.push(option)
    } else {
      // Remove the option if already selected
      currentValues.splice(index, 1)
    }

    const updatedFormData = { ...formData, [field]: currentValues }
    setFormData(updatedFormData)

    setTimeout(() => {
      if (dropdownScrollRef.current) {
        dropdownScrollRef.current.scrollTop = currentScrollTop
      }
    }, 0)

    applyCascadingFilters(updatedFormData)
  }

  const handleSelectAllDropdown = (field) => {
    const effectiveOptions =
      cascadingOptions[
        field === "siteCode"
          ? "site_codes"
          : field === "projectName"
            ? "project_names"
            : field === "state"
              ? "states"
              : field === "location"
                ? "locations"
                : "site_codes"
      ].length > 0
        ? cascadingOptions[
            field === "siteCode"
              ? "site_codes"
              : field === "projectName"
                ? "project_names"
                : field === "state"
                  ? "states"
                  : field === "location"
                    ? "locations"
                    : "site_codes"
          ]
        : originalDropdownOptions[
            field === "siteCode"
              ? "site_codes"
              : field === "projectName"
                ? "project_names"
                : field === "state"
                  ? "states"
                  : field === "location"
                    ? "locations"
                    : "site_codes"
          ]

    const searchTerm = searchTerms[field]
    const filteredOptions = searchTerm
      ? effectiveOptions.filter((option) => option.toLowerCase().includes(searchTerm.toLowerCase()))
      : effectiveOptions

    const updatedFormData = {
      ...formData,
      [field]: formData[field].length === filteredOptions.length ? [] : [...filteredOptions],
    }
    setFormData(updatedFormData)

    applyCascadingFilters(updatedFormData)
  }

  const handleRemoveOption = (field, option) => {
    const currentValues = [...formData[field]]
    const index = currentValues.indexOf(option)
    if (index !== -1) {
      currentValues.splice(index, 1)
      const updatedFormData = { ...formData, [field]: currentValues }
      setFormData(updatedFormData)
      applyCascadingFilters(updatedFormData)
    }
  }

  const handleClear = () => {
    const clearedFormData = {
      siteCode: [],
      projectName: [],
      state: [],
      location: [],
      preclosureRange: "",
    }
    setFormData(clearedFormData)
    setFromMonth(null)
    setToMonth(null)
    setPreclosure(false)
    handleRemoveFile()
    setTableData([])
    setCurrentPage(1)
    setSelectedRows(new Set())
    setShowPayrollSection(false)
    setPayrollFile(null)

    setSearchTerms({
      siteCode: "",
      projectName: "",
      state: "",
      location: "",
      preclosureRange: "",
    })

    // Reset dropdown options to show all original options
    if (originalDataset.length > 0) {
      const site_codes = [
        ...new Set(
          originalDataset.map((item) =>
            getFieldValue(item, ["Project_code", "project_code", "site_code", "Site_code"]),
          ),
        ),
      ].filter(Boolean)

      const project_names = [
        ...new Set(
          originalDataset.map((item) => getFieldValue(item, ["Project_Name", "project_name", "Project_name"])),
        ),
      ].filter(Boolean)

      const states = [...new Set(originalDataset.map((item) => getFieldValue(item, ["State", "state"])))].filter(
        Boolean,
      )

      const locations = [
        ...new Set(
          originalDataset.map((item) =>
            getFieldValue(item, ["Site_Location", "location", "Location", "site_location"]),
          ),
        ),
      ].filter(Boolean)

      const finalOptions = {
        site_codes,
        project_names,
        states,
        locations,
        pro_closure_date_range: dropdownOptions.pro_closure_date_range,
      }

      setDropdownOptions(finalOptions)
      setCascadingOptions(finalOptions)
    }
  }

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get("https://api.rcube.tickbox.co.in/api/projectcodeFilter/", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "arraybuffer",
      })

      // Extract content-disposition header
      const contentDisposition = response.headers["content-disposition"] || response.headers["Content-Disposition"]

      // Use helper function to download the template
      downloadExcelFile(response.data, "Site_Selection_Template.xlsx", contentDisposition)

      showSuccess("Download Successful", "Site selection template downloaded successfully.", {
        life: 5000,
        sticky: false,
      })
    } catch (error) {
      console.error("Download failed:", error)
      if (error.response?.status === 500) {
        showError("Download Failed", "SOMETHING WENT WRONG!!!")
      } else {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.detail ||
          "Failed to download template. Please try again."
        showError("Download Failed", errorMessage)
      }
    }
  }

  // Custom dropdown component for multi-select
  const CustomDropdown = ({ label, field, options }) => {
    const searchTerm = searchTerms[field]

    const effectiveOptions =
      cascadingOptions[
        field === "siteCode"
          ? "site_codes"
          : field === "projectName"
            ? "project_names"
            : field === "state"
              ? "states"
              : field === "location"
                ? "locations"
                : "site_codes"
      ].length > 0
        ? cascadingOptions[
            field === "siteCode"
              ? "site_codes"
              : field === "projectName"
                ? "project_names"
                : field === "state"
                  ? "states"
                  : field === "location"
                    ? "locations"
                    : "site_codes"
          ]
        : options

    // Filter options based on search term for all fields
    const filteredOptions = searchTerm
      ? effectiveOptions.filter((option) => option.toLowerCase().includes(searchTerm.toLowerCase()))
      : effectiveOptions

    const getSearchPlaceholder = (field) => {
      switch (field) {
        case "siteCode":
          return "Search site codes..."
        case "projectName":
          return "Search project names..."
        case "state":
          return "Search states..."
        case "location":
          return "Search locations..."
        default:
          return "Search..."
      }
    }

    return (
      <div className="relative w-full">
        <label className="block mb-2 text-sm font-medium text-white">{label}</label>

        {/* Selected options display */}
        <div
          className="flex flex-wrap gap-1 min-h-[42px] p-2 rounded-xl bg-white/10 border border-white/20 cursor-pointer w-full"
          onClick={() => toggleDropdown(field)}
        >
          {formData[field].length > 0 ? (
            formData[field].map((option, idx) => (
              <div key={idx} className="flex items-center bg-white/20 text-white text-xs rounded-full px-2 py-1">
                <span className="mr-1 truncate max-w-[100px] sm:max-w-[120px]">{option}</span>
                <X
                  size={14}
                  className="cursor-pointer hover:text-red-400 flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveOption(field, option)
                  }}
                />
              </div>
            ))
          ) : (
            <span className="text-white/60 text-sm">Select {label}</span>
          )}

          <div className="ml-auto flex items-center flex-shrink-0">
            <ChevronDown
              size={16}
              className={`text-white/60 transition-transform ${openDropdown === field ? "rotate-180" : ""}`}
            />
          </div>
        </div>

        {/* Dropdown menu */}
        {openDropdown === field && (
          <>
            {/* Backdrop to handle clicks outside */}
            <div
              className="fixed inset-0 z-[9998]"
              onClick={(e) => {
                // Don't close if clicking on search input or its container
                if (e.target.closest(".search-container")) {
                  return
                }
                setOpenDropdown(null)
                setIsAnyDropdownOpen(false)
                updateDropdownOptions(formData)
              }}
            />

            <div
              ref={dropdownScrollRef}
              className="absolute mt-1 w-full max-h-60 overflow-auto rounded-xl backdrop-blur-md bg-gray-800 border border-white/20 shadow-lg z-[9999]"
              onScroll={(e) => {
                scrollPositions.current[field] = e.target.scrollTop
              }}
            >
              <div className="sticky top-0 bg-gray-900 p-2 border-b border-white/20 search-container">
                <div className="relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => handleSearchTermChange(field, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder={getSearchPlaceholder(field)}
                    className="w-full p-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className="sticky top-0 bg-white/20 backdrop-blur-md border-b border-white/20 p-2">
                <div
                  className="cursor-pointer hover:bg-[#d97925] rounded-lg p-2 transition-colors"
                  onClick={() => handleSelectAllDropdown(field)}
                >
                  <span className="text-white text-sm">
                    {formData[field].length === effectiveOptions.length ? "Deselect All" : "Select All"}
                  </span>
                </div>
              </div>

              {filteredOptions.map((option, idx) => (
                <div
                  key={idx}
                  className={`cursor-pointer p-2 hover:bg-[#d97925] transition-colors ${
                    formData[field].includes(option) ? "bg-white/15" : ""
                  }`}
                  onClick={() => handleOptionSelect(field, option)}
                >
                  <div className="flex items-center">
                    <div
                      className={`w-4 h-4 mr-2 rounded border flex-shrink-0 ${
                        formData[field].includes(option) ? "bg-orange-500 border-orange-500" : "border-white/40"
                      }`}
                    >
                      {formData[field].includes(option) && (
                        <div className="flex items-center justify-center h-full">
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M9 1L3.5 6.5L1 4"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    <span className="text-white text-sm truncate">{option}</span>
                  </div>
                </div>
              ))}

              {filteredOptions.length === 0 && (
                <div className="p-4 text-center text-white/60">No matching {label.toLowerCase()} found</div>
              )}
            </div>
          </>
        )}
      </div>
    )
  }

  // Custom dropdown component for single-select (preclosure)
  const SingleSelectDropdown = ({ label, field, options, required = false }) => {
    return (
      <div className="relative w-full">
        <label className="block mb-2 text-sm font-medium text-white">
          {label} {required && <span className="text-red-500">*</span>}
        </label>

        <div
          className="flex items-center justify-between min-h-[42px] p-2 rounded-xl bg-white/10 border border-white/20 cursor-pointer w-full"
          onClick={() => toggleDropdown(field)}
        >
          <span className="text-white text-sm truncate">
            {formData[field] ? formData[field] : <span className="text-white/60">Select {label}</span>}
          </span>

          <ChevronDown
            size={16}
            className={`text-white/60 transition-transform flex-shrink-0 ml-2 ${openDropdown === field ? "rotate-180" : ""}`}
          />
        </div>

        {openDropdown === field && (
          <>
            {/* Backdrop to handle clicks outside */}
            <div
              className="fixed inset-0 z-[9998]"
              onClick={() => {
                setOpenDropdown(null)
                setIsAnyDropdownOpen(false)
              }}
            />

            <div className="absolute mt-1 w-full max-h-48 overflow-auto rounded-xl backdrop-blur-md bg-white/10 border border-white/20 shadow-lg z-[9999]">
              <div
                className="cursor-pointer p-2 hover:bg-[#d97925] transition-colors"
                onClick={() => {
                  setFormData({ ...formData, [field]: "" })
                  setOpenDropdown(null)
                }}
              >
                <span className="text-white text-sm">Select {label}</span>
              </div>

              {options.map((opt, index) => (
                <div
                  key={index}
                  className={`cursor-pointer p-2 hover:bg-[#d97925] transition-colors ${
                    formData[field] === opt ? "bg-white/15" : ""
                  }`}
                  onClick={() => {
                    setFormData({ ...formData, [field]: opt })
                    setOpenDropdown(null)
                  }}
                >
                  <span className="text-white text-sm">{opt}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    )
  }

  const handleRemoveFile = () => {
    setUploadedFile(null)
    document.getElementById("site-upload").value = null
    showInfo("File Removed", "Site template has been removed.")
  }

  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (file && file.name.endsWith(".xlsx")) {
      setUploadedFile(file)
    } else {
      e.target.value = null
      showError("Invalid File", "Please upload a valid .xlsx file.")
    }
  }

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1))
  }

  const getPageNumbers = () => {
    const pageNumbers = []
    for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
      pageNumbers.push(i)
    }
    return pageNumbers
  }

  const goToPage = (page) => {
    setCurrentPage(page)
  }

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
  }

  return (
    <div className="min-h-screen">
      {/* Toast Component */}
      <Toast ref={toast} position="top-right" />

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-md z-[10000] flex items-center justify-center">
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Success</h3>
              <p className="text-white/80 mb-6">The payload dump is submitted successfully</p>
              <button
                onClick={handleSuccessModalOk}
                className="px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {(isLoading || isDownloadingPayroll || isUploadingPayroll) && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-md z-50 flex items-center justify-center">
          <ClimbingBoxLoader color="#d97925" size={20} />
        </div>
      )}

      {/* Container 1: Filter Fields - Moved to top */}
      <div className="p-2 sm:p-4 lg:p-6 pb-0">
        <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 w-full max-w-7xl mx-auto text-white">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
            <h2 className="text-lg sm:text-xl font-semibold">Filters</h2>
          </div>

          {/* Row 1 - Filter Dropdowns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 mb-4">
            <CustomDropdown label="Site Code" field="siteCode" options={dropdownOptions.site_codes} />
            <CustomDropdown label="Project Name" field="projectName" options={dropdownOptions.project_names} />
            <CustomDropdown label="State" field="state" options={dropdownOptions.states} />
            <CustomDropdown label="Location" field="location" options={dropdownOptions.locations} />
          </div>

          {/* Row 2 - Date Fields */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 mb-4">
            {/* From Date */}
            <div className="w-full">
              <label className="block mb-2 text-sm font-medium text-white">From (MMM/YYYY)<span className="text-red-500"> *</span></label>
              <DatePicker
                views={["year", "month"]}
                value={fromMonth}
                onChange={(newValue) => {
                  const newFromMonth = newValue ? dayjs(newValue).startOf("month") : null
                  setFromMonth(newFromMonth)
                  if (newFromMonth) {
                    setToMonth(newFromMonth)
                  } else {
                    setToMonth(null)
                  }
                }}
                format="MMM/YYYY"
                slots={{ openPickerIcon: () => <CalendarDays className="text-orange-500" /> }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    InputProps: {
                      style: {
                        color: "#fff",
                        backgroundColor: "rgba(255,255,255,0.1)",
                        borderRadius: 12,
                      },
                    },
                  },
                }}
              />
            </div>

            {/* To Date */}
            <div className="w-full">
              <label className="block mb-2 text-sm font-medium text-white">To (MMM/YYYY)<span className="text-red-500"> *</span></label>
              <DatePicker
                views={["year", "month"]}
                value={toMonth}
                onChange={(newValue) => setToMonth(newValue ? dayjs(newValue).startOf("month") : null)}
                format="MMM/YYYY"
                defaultCalendarMonth={fromMonth ? dayjs(fromMonth) : undefined}
                minDate={fromMonth ? dayjs(fromMonth).startOf("month") : undefined}
                maxDate={fromMonth ? dayjs(fromMonth).endOf("month") : undefined}
                disabled={!fromMonth}
                slots={{ openPickerIcon: () => <CalendarDays className="text-orange-500" /> }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    InputProps: {
                      style: {
                        color: "#fff",
                        backgroundColor: fromMonth ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.05)",
                        borderRadius: 12,
                      },
                    },
                    placeholder: fromMonth ? "Select To Date" : "Select From Date first",
                  },
                }}
              />
            </div>
          </div>

          {/* Row 3 - Preclosure, Download, Upload */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 mb-4">
            {/* Preclosure Section */}
            <div className="w-full">
              <div className="mb-4">
                <FormControlLabel
                  control={
                    <Switch checked={preclosure} onChange={(e) => setPreclosure(e.target.checked)} color="warning" />
                  }
                  label={<Typography className="text-white font-medium text-sm sm:text-base">Preclosure</Typography>}
                />
              </div>

              {/* Preclosure Date Range */}
              {preclosure && (
                <SingleSelectDropdown
                  label="Preclosure Date Range"
                  field="preclosureRange"
                  options={dropdownOptions.pro_closure_date_range}
                  required={true}
                />
              )}
            </div>

            {/* Download Template */}
            <div className="flex flex-col justify-center w-full">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleDownloadTemplate}
                  className="p-3 rounded-full backdrop-blur-md bg-white/10 border border-white/20 shadow-lg transition hover:bg-white/20 group flex-shrink-0"
                  title="Download Template"
                >
                  <Download className="text-white group-hover:text-[#d97925]" />
                </button>
                <span className="text-white text-sm">Download Site Template</span>
              </div>
            </div>

            {/* Upload Template */}
            <div className="flex flex-col justify-center w-full">
              <div className="flex items-start space-x-3">
                <label
                  htmlFor="site-upload"
                  className="p-3 rounded-full cursor-pointer backdrop-blur-md bg-white/10 border border-white/20 shadow-lg transition hover:bg-white/20 group flex-shrink-0"
                  title="Select Template File"
                >
                  <Upload className="text-white group-hover:text-[#d97925]" />
                </label>
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-white text-sm">Upload Site Template</span>
                  {uploadedFile && (
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-white text-xs truncate">{uploadedFile.name}</span>
                      <button
                        onClick={handleRemoveFile}
                        className="text-white hover:text-red-500 transition text-xs flex-shrink-0"
                        title="Remove file"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                <input type="file" id="site-upload" accept=".xlsx" onChange={handleFileUpload} hidden />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-end">
            <button
              onClick={handleClear}
              className="px-4 sm:px-6 py-2 rounded-full text-white font-medium backdrop-blur-md bg-white/10 border border-white/20 shadow-lg transition hover:bg-white/20 hover:text-red-400 w-full sm:w-auto"
              disabled={isLoading}
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="px-4 sm:px-6 py-2 rounded-full text-white font-medium backdrop-blur-md bg-white/10 border border-white/20 shadow-lg transition hover:bg-white/20 hover:text-[#d97925] w-full sm:w-auto"
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "Apply"}
            </button>
          </div>
        </div>
      </div>

      {/* Container 2: Table with Pagination */}
      {tableData.length > 0 && (
        <div className={`p-2 sm:p-4 lg:p-6 pt-4 transition-all duration-300 ${isAnyDropdownOpen ? "mt-16" : ""}`}>
          <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 w-full max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4 gap-2">
              <h2 className="text-lg sm:text-xl font-semibold text-white">Filtered Data</h2>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-sm">
                <span className="text-white">
                  Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                  {Math.min(currentPage * rowsPerPage, tableData.length)} of {tableData.length} entries
                </span>
                <span className="text-white">Selected: {selectedRows.size}</span>
              </div>
            </div>

            {/* Table Container with Dynamic Height */}
            <div className="overflow-x-auto">
              <div
                className={`${
                  paginatedData.length <= 6
                    ? "h-auto min-h-[200px]"
                    : paginatedData.length <= 10
                      ? "h-[400px]"
                      : "h-[300px] sm:h-[400px] lg:h-[600px]"
                } overflow-y-auto rounded-lg border border-white/10`}
              >
                <table className="w-full text-white min-w-[800px]">
                  <thead className="sticky top-0 bg-white/20 backdrop-blur-md border-b border-white/20 z-10">
                    <tr>
                      <th className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-semibold text-white text-center whitespace-nowrap w-12 sm:w-16">
                        S.No
                      </th>
                      <th className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-semibold text-white text-center whitespace-nowrap w-20 sm:w-24">
                        MMM YYYY
                      </th>
                      <th className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-semibold text-white text-left whitespace-nowrap">
                        Project Code
                      </th>
                      <th className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-semibold text-white text-left whitespace-nowrap">
                        Project Name
                      </th>
                      <th className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-semibold text-white text-left whitespace-nowrap">
                        State
                      </th>
                      <th className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-semibold text-white text-left whitespace-nowrap">
                        Location
                      </th>
                      <th className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-semibold text-white text-center whitespace-nowrap w-12 sm:w-16">
                        <input
                          type="checkbox"
                          checked={selectedRows.size === tableData.length && tableData.length > 0}
                          onChange={handleSelectAll}
                          className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.map((row, rowIndex) => {
                      const actualRowIndex = (currentPage - 1) * rowsPerPage + rowIndex
                      return (
                        <tr key={actualRowIndex} className="border-b border-white/10 hover:bg-white/5 transition">
                          <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm whitespace-nowrap text-center font-medium text-orange-300">
                            {actualRowIndex + 1}
                          </td>
                          <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm whitespace-nowrap text-center font-medium text-orange-300">
                            {getTableFieldValue(row, "period")}
                          </td>
                          <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm whitespace-nowrap">
                            {getTableFieldValue(row, "project_code") || "-"}
                          </td>
                          <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm whitespace-nowrap">
                            {getTableFieldValue(row, "project_name") || "-"}
                          </td>
                          <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm whitespace-nowrap">
                            {getTableFieldValue(row, "state") || "-"}
                          </td>
                          <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm whitespace-nowrap">
                            {getTableFieldValue(row, "location") || "-"}
                          </td>
                          <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm whitespace-nowrap text-center">
                            <input
                              type="checkbox"
                              checked={selectedRows.has(actualRowIndex)}
                              onChange={() => handleRowSelect(actualRowIndex)}
                              className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Enhanced Pagination Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 sm:mt-6 gap-4">
              <div className="text-white text-sm order-2 sm:order-1">
                Page {currentPage} of {totalPages}
              </div>

              <div className="flex items-center space-x-1 sm:space-x-2 order-1 sm:order-2">
                {/* Previous Button */}
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className="p-1 sm:p-2 rounded-md bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>

                {/* Page Numbers */}
                {getPageNumbers().map((page) => (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`px-2 sm:px-3 py-1 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition ${
                      currentPage === page ? "bg-orange-500 text-white" : "bg-white/10 text-white hover:bg-white/20"
                    }`}
                  >
                    {page}
                  </button>
                ))}

                {/* Next Button */}
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="p-1 sm:p-2 rounded-md bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              </div>

              <div className="text-white text-sm order-3">{tableData.length} total records</div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-end mt-4">
              <button
                onClick={handleSubmit}
                disabled={selectedRows.size === 0}
                className="px-4 sm:px-6 py-2 rounded-full text-white font-medium bg-orange-600 hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                Submit
              </button>
              <button
                onClick={handleExport}
                disabled={selectedRows.size === 0}
                className="px-4 sm:px-6 py-2 rounded-full text-white font-medium backdrop-blur-md bg-white/10 border border-white/20 shadow-lg transition hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                Export
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Container 3: Payroll Dump Section */}
      {showPayrollSection && (
        <div className="p-2 sm:p-4 lg:p-6 pt-4">
          <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 w-full max-w-7xl mx-auto">
            <h2 className="text-lg sm:text-xl font-semibold text-white mb-4 sm:mb-6">Payroll Dump</h2>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              {/* Download Payroll Dump Template */}
              <div className="flex flex-col justify-center w-full">
                <div className="flex items-start space-x-3">
                  <button
                    onClick={handleDownloadPayrollTemplate}
                    disabled={isDownloadingPayroll || (!formData.preclosureRange && preclosure) || !client_id}
                    className="p-3 rounded-full backdrop-blur-md bg-white/10 border border-white/20 shadow-lg transition hover:bg-white/20 group disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                    title="Download Payroll Dump Template"
                  >
                    <Download className="text-white group-hover:text-[#d97925]" />
                  </button>
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="text-white text-sm">
                      {isDownloadingPayroll ? "Downloading..." : "Download Payroll Dump Template"}
                    </span>
                    <span className="text-white/60 text-xs">
                      Period: {fromMonth ? dayjs(fromMonth).format("YYYY-MM-01") : dayjs().format("YYYY-MM-01")}
                      {preclosure && formData.preclosureRange ? `, Preclosure: ${formData.preclosureRange}` : ""}
                    </span>
                  </div>
                </div>
              </div>

              {/* Upload Payroll Dump Template */}
              <div className="flex flex-col justify-center w-full">
                <div className="flex items-start space-x-3">
                  <label
                    htmlFor="payroll-upload"
                    className="p-3 rounded-full cursor-pointer backdrop-blur-md bg-white/10 border border-white/20 shadow-lg transition hover:bg-white/20 group flex-shrink-0"
                    title="Upload Payroll Dump Template"
                  >
                    <Upload className="text-white group-hover:text-[#d97925]" />
                  </label>
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="text-white text-sm">
                      Upload Payroll Dump Template (.xlsx) <span className="text-red-500">*</span>
                    </span>
                    <span className="text-white/60 text-xs">
                      Selected rows: {selectedRows.size} | Required for upload
                    </span>
                    {payrollFile && (
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-white text-xs truncate">{payrollFile.name}</span>
                        <button
                          onClick={handleRemovePayrollFile}
                          className="text-white hover:text-red-500 transition text-xs flex-shrink-0"
                          title="Remove file"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                  <input type="file" id="payroll-upload" accept=".xlsx" onChange={handlePayrollFileUpload} hidden />
                </div>
              </div>
            </div>

            {/* Final Submit and Clear Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={handleFinalSubmit}
                disabled={!payrollFile || selectedRows.size === 0 || isUploadingPayroll}
                className="px-6 sm:px-8 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                {isUploadingPayroll ? "UPLOADING..." : "FINAL SUBMIT"}
              </button>
              <button
                onClick={() => setShowPayrollSection(false)}
                className="px-6 sm:px-8 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition w-full sm:w-auto"
                disabled={isUploadingPayroll}
              >
                CLEAR
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Revision
